const EMPTY_THRESHOLD = { min: '', max: '' };

export const WATER_THRESHOLD_KEYS = ['ph', 'ec', 'orp', 'temp', 'level', 'volume'];
export const EDITABLE_WATER_THRESHOLD_KEYS = WATER_THRESHOLD_KEYS.filter((key) => key !== 'volume');

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function toDraftValue(value) {
  const resolved = firstDefined(value);
  return resolved === undefined ? '' : String(resolved);
}

function readByAliases(source = {}, aliases = []) {
  for (const key of aliases) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function rangeFromAliases(source = {}, minAliases = [], maxAliases = []) {
  const min = readByAliases(source, minAliases);
  const max = readByAliases(source, maxAliases);
  if (min === undefined && max === undefined) return undefined;
  return [min ?? '', max ?? ''];
}

export function parseWaterThresholdValue(raw) {
  if (Array.isArray(raw)) {
    return { min: raw[0] ?? '', max: raw[1] ?? '' };
  }
  if (typeof raw === 'string') {
    const parts = raw
      .split(/\s*-\s*|,|;/)
      .map((item) => item.trim())
      .filter(Boolean);
    return { min: parts[0] ?? '', max: parts[1] ?? '' };
  }
  if (typeof raw === 'number') return { min: '', max: String(raw) };
  return { ...EMPTY_THRESHOLD };
}

export function serializeWaterThreshold(value = {}) {
  const min = String(value.min ?? '').trim();
  const max = String(value.max ?? '').trim();
  if (!min && !max) return null;
  if (!min) return max;
  if (!max) return min;
  return `${min}-${max}`;
}

export function buildWaterThresholdState(thresholds = {}, config = {}) {
  const thresholdAliases = {
    ph: ['ph', 'pH', 'water_ph'],
    ec: ['ec', 'EC', 'water_ec'],
    orp: ['orp', 'ORP', 'water_orp'],
    temp: ['temp', 'temperature', 'water_temp', 'water_temperature', 'watertemp'],
    level: ['level', 'water_level', 'waterlevel'],
    volume: ['volume', 'tank_volume', 'water_volume'],
  };
  const configThresholds = {
    ph: rangeFromAliases(
      config,
      ['phMinThreshold', 'phLo', 'pHLo', 'pHLow', 'phMin', 'pHMin'],
      ['phMaxThreshold', 'phHi', 'pHHi', 'pHHigh', 'phMax', 'pHMax']
    ),
    ec: rangeFromAliases(
      config,
      ['ecMinThreshold', 'ecLo', 'ECLo', 'ecLow', 'ecMin', 'ECMin'],
      ['ecMaxThreshold', 'ecHi', 'ECHi', 'ecHigh', 'ecMax', 'ECMax']
    ),
    orp: rangeFromAliases(
      config,
      ['orpMinThreshold', 'orpLo', 'ORPLo', 'orpLow', 'orpMin', 'ORPMin'],
      ['orpMaxThreshold', 'orpHi', 'ORPHi', 'orpHigh', 'orpMax', 'ORPMax']
    ),
    temp: rangeFromAliases(
      config,
      [
        'waterTempMinThreshold',
        'waterTemperatureMinThreshold',
        'temperatureMinThreshold',
        'tempMinThreshold',
        'waterTempLo',
        'tempLo',
        'TempLo',
        'tempMin',
      ],
      [
        'waterTempMaxThreshold',
        'waterTemperatureMaxThreshold',
        'temperatureMaxThreshold',
        'tempMaxThreshold',
        'waterTempHi',
        'tempHi',
        'TempHi',
        'tempMax',
      ]
    ),
    level: rangeFromAliases(
      config,
      [
        'waterLevelMinThreshold',
        'levelMinThreshold',
        'waterLevelMin',
        'levelLo',
        'waterLevelLo',
        'levelMin',
      ],
      [
        'waterLevelMaxThreshold',
        'levelMaxThreshold',
        'waterLevelMax',
        'levelHi',
        'waterLevelHi',
        'levelMax',
      ]
    ),
    volume: rangeFromAliases(
      config,
      ['volumeLo', 'tankVolumeLo', 'volumeMin'],
      ['volumeHi', 'volumeMax']
    ),
  };

  return Object.fromEntries(
    WATER_THRESHOLD_KEYS.map((key) => [
      key,
      parseWaterThresholdValue(firstDefined(readByAliases(thresholds, thresholdAliases[key]), configThresholds[key])),
    ])
  );
}

export function buildEmptyWaterThresholdState() {
  return Object.fromEntries(WATER_THRESHOLD_KEYS.map((key) => [key, { ...EMPTY_THRESHOLD }]));
}

export function buildWaterConfigDraft(config = {}, device = {}) {
  const tank = config.tank && typeof config.tank === 'object' ? config.tank : {};
  const intervals = config.intervals && typeof config.intervals === 'object' ? config.intervals : {};
  return {
    tankMaxHeight: toDraftValue(firstDefined(config.tankMaxHeight, tank.maxHeight, device.tankMaxHeight)),
    tankFullVolume: toDraftValue(firstDefined(config.tankFullVolume, tank.fullVolume, device.tankFullVolume)),
    dMin: toDraftValue(firstDefined(config.displayInterval, config.dMin, config.display_min, intervals.display_min)),
    sMin: toDraftValue(firstDefined(config.serverInterval, config.sMin, config.server_min, intervals.server_min)),
  };
}

export function serializeWaterConfig(draft = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(draft)) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    const numeric = Number(text);
    if (Number.isFinite(numeric)) payload[key] = numeric;
  }
  return payload;
}
